import React, { useState, useEffect } from 'react';
import './PayrollTable.css';

const API = '/api/payroll';

const CATEGORIES = [
  { value: 'admin',   label: 'Администратор' },
  { value: 'manager', label: 'Управляющий' },
  { value: 'spir',    label: 'СПИР' },
  { value: 'maid',    label: 'Горничная' },
  { value: 'tech',    label: 'Техник' },
  { value: 'office',  label: 'Офис' },
  { value: 'ota',     label: 'ОТА' },
  { value: 'ob',      label: 'Отдел бронирования' },
];

const PAY_TYPES = [
  { value: 'shift',         label: 'Ставка за смену' },
  { value: 'salary',        label: 'Оклад' },
  { value: 'salary_kpi',    label: 'Оклад + KPI' },
  { value: 'shift_percent', label: 'Смена + %' },
];

const EMPTY = {
  full_name: '', inn: '', phone: '', position: '', category: 'admin',
  hotel_id: '', department_id: '', pay_type: 'shift',
  rate_per_shift: 0, salary: 0, kpi_rate: 0,
};

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [hotels, setHotels]       = useState([]);
  const [depts, setDepts]         = useState([]);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [showHotels, setShowHotels] = useState(null); // employee id
  const [empHotels, setEmpHotels]   = useState([]);
  const [filter, setFilter]       = useState({ hotel_id: '', dept_id: '' });
  const [loading, setLoading]     = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function fireEmployee(empId) {
    if (!window.confirm('Уволить сотрудника? Он останется в истории ведомостей.')) return;
    await fetch(`${API}/employees/${empId}/fire`, { method: 'POST' });
    loadAll();
  }

  async function restoreEmployee(empId) {
    await fetch(`${API}/employees/${empId}/restore`, { method: 'POST' });
    loadAll();
  }

  async function loadEmpHotels(empId) {
    const data = await fetch(`${API}/employees/${empId}/hotels`).then(r => r.json());
    setEmpHotels(Array.isArray(data) ? data : []);
  }

  async function addEmpHotel(empId, hotelId, isPrimary, supplement = 0) {
    await fetch(`${API}/employees/${empId}/hotels?hotel_id=${hotelId}&is_primary=${isPrimary}&salary_supplement=${supplement}`, { method: 'POST' });
    loadEmpHotels(empId);
    if (isPrimary) loadAll();
  }

  async function removeEmpHotel(empId, hotelId) {
    await fetch(`${API}/employees/${empId}/hotels/${hotelId}`, { method: 'DELETE' });
    loadEmpHotels(empId);
  }

  async function loadAll() {
    const [e, h, d] = await Promise.all([
      fetch(`${API}/employees`).then(r => r.json()),
      fetch(`${API}/hotels`).then(r => r.json()),
      fetch(`${API}/departments`).then(r => r.json()),
    ]);
    setEmployees(e); setHotels(h); setDepts(d);
  }

  async function loadEmployees() {
    let url = `${API}/employees?`;
    if (filter.hotel_id) url += `hotel_id=${filter.hotel_id}&`;
    if (filter.dept_id)  url += `department_id=${filter.dept_id}`;
    const data = await fetch(url).then(r => r.json());
    setEmployees(data);
  }

  useEffect(() => { loadEmployees(); }, [filter]);

  function openNew() { setForm(EMPTY); setEditId(null); setShowForm(true); }

  function openEdit(emp) {
    setForm({
      full_name: emp.full_name, inn: emp.inn || '', phone: emp.phone || '', position: emp.position || '',
      category: emp.category, hotel_id: emp.hotel_id || '',
      department_id: emp.department_id || '', pay_type: emp.pay_type,
      rate_per_shift: emp.rate_per_shift, salary: emp.salary, kpi_rate: emp.kpi_rate,
    });
    setEditId(emp.id); setShowForm(true);
  }

  async function save() {
    setLoading(true);
    const body = {
      ...form,
      hotel_id: form.hotel_id || null,
      department_id: form.department_id || null,
      rate_per_shift: parseFloat(form.rate_per_shift) || 0,
      salary: parseFloat(form.salary) || 0,
      kpi_rate: parseFloat(form.kpi_rate) || 0,
    };
    const url    = editId ? `${API}/employees/${editId}` : `${API}/employees`;
    const method = editId ? 'PATCH' : 'POST';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    setShowForm(false); loadAll(); setLoading(false);
  }

  async function deactivate(id) {
    if (!window.confirm('Деактивировать сотрудника?')) return;
    await fetch(`${API}/employees/${id}`, { method: 'DELETE' });
    loadAll();
  }

  const payLabel = v => PAY_TYPES.find(p => p.value === v)?.label || v;
  const catLabel = v => CATEGORIES.find(c => c.value === v)?.label || v;

  return (
    <div className="pt-page">
      <div className="pt-header">
        <h2>Сотрудники</h2>
        <button className="btn-primary" onClick={openNew}>+ Добавить</button>
      </div>

      <div className="pt-filters">
        <select value={filter.hotel_id} onChange={e => setFilter(f => ({ ...f, hotel_id: e.target.value }))}>
          <option value="">Все отели</option>
          {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
        </select>
        <select value={filter.dept_id} onChange={e => setFilter(f => ({ ...f, dept_id: e.target.value }))}>
          <option value="">Все отделы</option>
          {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>

      <table className="pt-table">
        <thead>
          <tr>
            <th>ФИО</th><th>ИНН</th><th>Должность</th><th>Категория</th><th>Скрыть из табеля</th>
            <th>Объект</th><th>Тип оплаты</th><th>Ставка</th><th>Оклад</th><th></th>
          </tr>
        </thead>
        <tbody>
          {employees.map(e => (
            <tr key={e.id}>
              <td><strong>{e.full_name}</strong></td>
              <td>{e.inn || '—'}</td>
              <td>{e.position || '—'}</td>
              <td><span className={`badge cat-${e.category}`}>{catLabel(e.category)}</span></td>
              <td>{e.hotel_name || e.department_name || '—'}</td>
              <td>{payLabel(e.pay_type)}</td>
              <td>{e.rate_per_shift > 0 ? `${e.rate_per_shift.toLocaleString()} ₽` : '—'}</td>
              <td>{e.salary > 0 ? `${e.salary.toLocaleString()} ₽` : '—'}</td>
              <td>
                <button className="btn-icon" onClick={() => openEdit(e)}>✏️</button>
                <button className="btn-icon" title="Отели" onClick={() => { setShowHotels(e.id); loadEmpHotels(e.id); }}>🏨</button>
                <button className="btn-icon danger" title="Уволить" onClick={() => fireEmployee(e.id)}>🔴</button>
                <button className="btn-icon danger" onClick={() => deactivate(e.id)}>✕</button>
              </td>
              <td style={{ textAlign:'center' }}>
                <input
                  type="checkbox"
                  checked={!!e.hide_from_schedule}
                  title={e.hide_from_schedule ? 'Скрыт из табеля' : 'Виден в табеле'}
                  onChange={async ev => {
                    await fetch(`${API}/employees/${e.id}/hide-schedule?hide=${ev.target.checked}`, { method: 'PATCH' });
                    loadAll();
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editId ? 'Редактировать сотрудника' : 'Новый сотрудник'}</h3>
              <button className="btn-icon" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>ФИО *</label>
                <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Иванов Иван Иванович" />
              </div>
              <div className="form-row">
                <label>ИНН</label>
                <input value={form.inn} onChange={e => setForm(f => ({ ...f, inn: e.target.value }))} placeholder="123456789012" maxLength={12} />
              </div>
              <div className="form-row">
                <label>Телефон</label>
                <input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+7 999 000 00 00" />
              </div>
              <div className="form-row">
                <label>Должность</label>
                <input value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} placeholder="Администратор" />
              </div>
              <div className="form-grid">
                <div className="form-row">
                  <label>Категория *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label>Тип оплаты *</label>
                  <select value={form.pay_type} onChange={e => setForm(f => ({ ...f, pay_type: e.target.value }))}>
                    {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-row">
                  <label>Отель</label>
                  <select value={form.hotel_id} onChange={e => setForm(f => ({ ...f, hotel_id: e.target.value }))}>
                    <option value="">— не выбран —</option>
                    {hotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <label>Отдел</label>
                  <select value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
                    <option value="">— не выбран —</option>
                    {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                {(form.pay_type === 'shift' || form.pay_type === 'shift_percent') && (
                  <div className="form-row">
                    <label>Ставка за смену (₽)</label>
                    <input type="number" value={form.rate_per_shift} onChange={e => setForm(f => ({ ...f, rate_per_shift: e.target.value }))} />
                  </div>
                )}
                {(form.pay_type === 'salary' || form.pay_type === 'salary_kpi') && (
                  <div className="form-row">
                    <label>Оклад (₽)</label>
                    <input type="number" value={form.salary} onChange={e => setForm(f => ({ ...f, salary: e.target.value }))} />
                  </div>
                )}
                {form.pay_type === 'salary_kpi' && (
                  <div className="form-row">
                    <label>KPI (₽)</label>
                    <input type="number" value={form.kpi_rate} onChange={e => setForm(f => ({ ...f, kpi_rate: e.target.value }))} />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Отмена</button>
              <button className="btn-primary" onClick={save} disabled={loading || !form.full_name}>
                {loading ? 'Сохраняем...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showHotels && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Отели сотрудника</h3>
              <button className="btn-icon" onClick={() => setShowHotels(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:13, color:'#6b7280', marginBottom:8 }}>Текущие отели:</div>
                {empHotels.length === 0 && <div style={{ color:'#9ca3af', fontSize:13 }}>Не привязан ни к одному отелю</div>}
                {empHotels.map(h => (
                  <div key={h.hotel_id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'#f9fafb', borderRadius:8, marginBottom:6 }}>
                    <div>
                      <span style={{ fontWeight: h.is_primary ? 600 : 400, color:'#111827' }}>{h.hotel_name}</span>
                      {h.is_primary && <span style={{ marginLeft:8, fontSize:11, background:'#d1fae5', color:'#065f46', padding:'2px 8px', borderRadius:20 }}>основной</span>}
                      {!h.is_primary && h.salary_supplement > 0 && (
                        <span style={{ marginLeft:8, fontSize:11, color:'#6b7280' }}>+{parseFloat(h.salary_supplement).toLocaleString()} ₽</span>
                      )}
                    </div>
                    <button className="btn-icon danger" onClick={() => removeEmpHotel(showHotels, h.hotel_id)}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:16 }}>
                <div style={{ fontSize:13, color:'#6b7280', marginBottom:8 }}>Добавить отель:</div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <select id="new-hotel-select" style={{ flex:1, padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14 }}>
                    <option value="">— выберите отель —</option>
                    {hotels.filter(h => !empHotels.find(eh => eh.hotel_id === h.id)).map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                  <input id="supplement-input" type="number" min="0" placeholder="Доплата ₽" style={{ width:110, padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:14 }} />
                  <button className="btn-secondary" onClick={() => {
                    const sel = document.getElementById('new-hotel-select');
                    const sup = document.getElementById('supplement-input');
                    if (sel.value) addEmpHotel(showHotels, parseInt(sel.value), false, parseFloat(sup.value) || 0);
                  }}>+ Доп.</button>
                  <button className="btn-primary" onClick={() => {
                    const sel = document.getElementById('new-hotel-select');
                    if (sel.value) addEmpHotel(showHotels, parseInt(sel.value), true, 0);
                  }}>★ Основной</button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setShowHotels(null)}>Готово</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
